import { useState, useEffect } from 'react';
import { getClients, createClient, updateClient, deleteClient, getClientDependencies, generateTrackingToken } from '../utils/api';
import { formatIQD } from '../utils/api';
import { Plus, Search, Users, X, Edit2, Trash2, AlertTriangle, FolderOpen, CreditCard, Package, FileText, CheckCircle, Link, Filter, UserCheck, Video } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const STATUS_MAP = {
  draft: 'مسودة', in_progress: 'جاري', delivered: 'مُسلَّم', completed: 'مكتمل',
  active: 'نشط', paused: 'موقوف', cancelled: 'ملغي',
  unpaid: 'غير مدفوعة', partial: 'مدفوعة جزئياً', overdue: 'متأخرة', pending: 'معلّقة',
};

export function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'subscriber' | 'client'
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', type: 'company', notes: '' });
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '', type: 'company', status: 'active', notes: '' });
  const navigate = useNavigate();

  // Dependency modal state
  const [deleteModal, setDeleteModal] = useState(null); // { client, deps, loading }
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = () => getClients({ search }).then(setClients).catch(() => toast.error('خطأ في تحميل العملاء'));
  useEffect(() => { load(); }, [search]); // eslint-disable-line

  const generateTrackingLink = async (clientId) => {
    try {
      await generateTrackingToken(clientId);
      toast.success('تم إنشاء رابط التتبع بنجاح');
      load();
    } catch (err) {
      toast.error('حدث خطأ أثناء إنشاء الرابط');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createClient(form);
      toast.success('تم إضافة العميل بنجاح');
      setShowForm(false);
      setForm({ name: '', phone: '', email: '', type: 'company', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ أثناء الإضافة'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name) return toast.error('الاسم مطلوب');
    try {
      await updateClient(editClient.id, editForm);
      toast.success('تم تحديث بيانات العميل');
      setEditClient(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'فشل التحديث'); }
  };

  // Step 1: Fetch dependencies BEFORE deleting
  const handleDeleteRequest = async (c) => {
    setDeleteModal({ client: c, deps: null, loading: true });
    try {
      const deps = await getClientDependencies(c.id);
      setDeleteModal({ client: c, deps, loading: false });
    } catch (err) {
      toast.error('فشل جلب بيانات العميل');
      setDeleteModal(null);
    }
  };

  // Step 2: Actually delete (force if needed)
  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleteLoading(true);
    try {
      await deleteClient(deleteModal.client.id, true); // force=true
      toast.success(`تم حذف العميل "${deleteModal.client.name}"`);
      setDeleteModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل حذف العميل');
    } finally {
      setDeleteLoading(false);
    }
  };

  const inputClasses = "w-full px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary focus:bg-white dark:bg-dark-deeper dark:focus:bg-dark-deeper focus:ring-4 focus:ring-primary/10";
  const labelClasses = "block text-xs font-bold text-gray-600 dark:text-gray-300 mb-1.5";

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-dark dark:text-gray-100 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl text-primary">
              <Users size={24} />
            </div>
            إدارة العملاء
            <span className="text-sm font-bold bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 px-2.5 py-1 rounded-lg ml-2">{clients.length}</span>
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">تصفح وإدارة بيانات جميع العملاء بكل سهولة.</p>
        </div>

        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-bold shadow-lg shadow-primary/30 hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-200"
        >
          {showForm ? <X size={18} /> : <Plus size={18} />}
          {showForm ? 'إلغاء الإضافة' : 'إضافة عميل'}
        </button>
      </div>

      {/* Toolbar: Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400">
            <Search size={18} />
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="ابحث بالاسم، الهاتف، أو البريد الإلكتروني..."
            className="w-full pl-4 pr-12 py-3.5 bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-2xl text-sm outline-none transition-all shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
        </div>
        <div className="flex items-center bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-2xl p-1 shadow-sm shrink-0">
          {[
            { key: 'all', label: 'الكل', icon: Users, count: clients.length },
            { key: 'subscriber', label: 'مشتركين', icon: UserCheck, count: clients.filter(c => c.type === 'subscriber').length },
            { key: 'client', label: 'عملاء', icon: Video, count: clients.filter(c => c.type !== 'subscriber').length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setTypeFilter(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 ${
                typeFilter === tab.key
                  ? 'bg-primary text-white shadow-md shadow-primary/25'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${
                typeFilter === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 dark:bg-white/10 text-gray-400'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={submit} className="bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-2xl p-6 shadow-sm animate-slide-down">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
            <div>
              <label className={labelClasses}>اسم العميل *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClasses} placeholder="مثال: شركة التقنية للتجارة" required />
            </div>
            <div>
              <label className={labelClasses}>رقم الهاتف</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClasses} placeholder="مثال: 0770..." dir="ltr" />
            </div>
            <div>
              <label className={labelClasses}>البريد الإلكتروني</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClasses} placeholder="email@company.com" dir="ltr" />
            </div>
            <div>
              <label className={labelClasses}>تصنيف العميل</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={`${inputClasses} cursor-pointer appearance-none`}>
                <option value="company">عميل (فيديو / فيتشر)</option>
                <option value="subscriber">مشترك (باقة شهرية)</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-white/5">
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 font-bold text-sm rounded-xl hover:bg-gray-200 transition-colors">إلغاء</button>
            <button type="submit" className="px-6 py-2.5 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary/90 shadow-md shadow-primary/20 transition-all">حفظ العميل</button>
          </div>
        </form>
      )}

      {/* Edit Modal */}
      {editClient && (
        <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onMouseDown={e => { if (e.target === e.currentTarget) setEditClient(null); }}>
          <div className="bg-white dark:bg-dark-deeper rounded-2xl w-full max-w-lg shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden flex flex-col animate-scale-up">
            <div className="p-5 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5/50">
              <h3 className="text-lg font-black text-dark dark:text-gray-100">تعديل بيانات العميل</h3>
              <button onClick={() => setEditClient(null)} className="p-2 bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 rounded-lg transition-colors"><X size={18} /></button>
            </div>
            <form onSubmit={handleEdit} className="p-6 flex flex-col gap-4">
              <div>
                <label className={labelClasses}>الاسم *</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required className={inputClasses} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses}>الهاتف</label>
                  <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className={inputClasses} dir="ltr" />
                </div>
                <div>
                  <label className={labelClasses}>البريد الإلكتروني</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className={inputClasses} dir="ltr" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses}>تصنيف العميل</label>
                  <select value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))} className={`${inputClasses} cursor-pointer appearance-none`}>
                    <option value="company">عميل (فيديو / فيتشر)</option>
                    <option value="subscriber">مشترك (باقة شهرية)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClasses}>الحالة</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} className={`${inputClasses} cursor-pointer appearance-none`}>
                    <option value="active">نشط (Active)</option>
                    <option value="inactive">غير نشط (Inactive)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClasses}>ملاحظات</label>
                <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} className={`${inputClasses} min-h-[80px] resize-y custom-scrollbar`} placeholder="أي ملاحظات إضافية..." />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => setEditClient(null)} className="px-5 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 font-bold text-sm rounded-xl hover:bg-gray-200 transition-colors">إلغاء</button>
                <button type="submit" className="px-6 py-2.5 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary/90 shadow-md shadow-primary/20 transition-all">حفظ التعديلات</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── DELETE CONFIRMATION MODAL WITH DEPENDENCY REPORT ─── */}
      {deleteModal && (
        <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onMouseDown={e => { if (e.target === e.currentTarget) setDeleteModal(null); }}>
          <div className="bg-white dark:bg-dark-deeper rounded-2xl w-full max-w-lg shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden flex flex-col animate-scale-up max-h-[85vh]">
            {/* Header */}
            <div className="p-5 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-red-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-danger/10 rounded-xl text-danger">
                  <AlertTriangle size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-dark dark:text-gray-100">حذف العميل</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{deleteModal.client.name}</p>
                </div>
              </div>
              <button onClick={() => setDeleteModal(null)} className="p-2 bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 rounded-lg transition-colors"><X size={18} /></button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {deleteModal.loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">جاري فحص ارتباطات العميل...</p>
                </div>
              ) : deleteModal.deps ? (
                <div className="space-y-4">
                  {/* Summary */}
                  {deleteModal.deps.can_delete ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-green-800">لا توجد ارتباطات نشطة</p>
                        <p className="text-xs text-green-600 mt-1">يمكن حذف هذا العميل بأمان. جميع المشاريع والاشتراكات مكتملة أو ملغية.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                      <AlertTriangle size={20} className="text-danger mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-red-800">⚠️ تنبيه: توجد ارتباطات نشطة</p>
                        <p className="text-xs text-red-600 mt-1">هذا العميل لديه بيانات نشطة. الحذف سيؤدي لفك ربط جميع البيانات (لن تُحذف لكن ستفقد ارتباطها بالعميل).</p>
                      </div>
                    </div>
                  )}

                  {/* Active Subscriptions */}
                  {deleteModal.deps.active_subscriptions.length > 0 && (
                    <div className="border border-orange-200 rounded-xl overflow-hidden">
                      <div className="bg-orange-50 px-4 py-3 flex items-center gap-2 border-b border-orange-200">
                        <Package size={16} className="text-orange-600" />
                        <span className="text-sm font-bold text-orange-800">اشتراكات نشطة ({deleteModal.deps.active_subscriptions.length})</span>
                      </div>
                      {deleteModal.deps.active_subscriptions.map(s => (
                        <div key={s.id} className="px-4 py-3 border-b border-orange-100 last:border-0 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-bold text-dark dark:text-gray-100">{s.package_name}</span>
                            <span className="text-xs text-gray-400 mr-2">({STATUS_MAP[s.status] || s.status})</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{s.period_start} → {s.period_end}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Active Projects */}
                  {deleteModal.deps.active_projects.length > 0 && (
                    <div className="border border-purple-200 rounded-xl overflow-hidden">
                      <div className="bg-purple-50 px-4 py-3 flex items-center gap-2 border-b border-purple-200">
                        <FolderOpen size={16} className="text-purple-600" />
                        <span className="text-sm font-bold text-purple-800">مشاريع جارية ({deleteModal.deps.active_projects.length})</span>
                      </div>
                      {deleteModal.deps.active_projects.map(p => (
                        <div key={p.id} className="px-4 py-3 border-b border-purple-100 last:border-0 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-bold text-dark dark:text-gray-100">{p.title}</span>
                            <span className="text-xs text-gray-400 mr-2">({STATUS_MAP[p.status] || p.status})</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{p.due_date || 'بدون موعد'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Unpaid Invoices */}
                  {deleteModal.deps.unpaid_invoices.length > 0 && (
                    <div className="border border-red-200 rounded-xl overflow-hidden">
                      <div className="bg-red-50 px-4 py-3 flex items-center gap-2 border-b border-red-200">
                        <FileText size={16} className="text-red-600" />
                        <span className="text-sm font-bold text-red-800">فواتير غير مدفوعة ({deleteModal.deps.unpaid_invoices.length})</span>
                      </div>
                      {deleteModal.deps.unpaid_invoices.map(inv => (
                        <div key={inv.id} className="px-4 py-3 border-b border-red-100 last:border-0 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-bold text-dark dark:text-gray-100">{inv.invoice_number || `#${inv.id}`}</span>
                            <span className="text-xs text-gray-400 mr-2">({STATUS_MAP[inv.payment_status] || inv.payment_status})</span>
                          </div>
                          <span className="text-xs font-bold text-danger">{formatIQD(inv.total_iqd - inv.paid_iqd)} متبقي</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Linked Deliverables */}
                  {deleteModal.deps.pending_deliverables.length > 0 && (
                    <div className="border border-yellow-200 rounded-xl overflow-hidden">
                      <div className="bg-yellow-50 px-4 py-3 flex items-center gap-2 border-b border-yellow-200">
                        <CreditCard size={16} className="text-yellow-600" />
                        <span className="text-sm font-bold text-yellow-800">تسليمات مرتبطة ({deleteModal.deps.pending_deliverables.length})</span>
                      </div>
                      {deleteModal.deps.pending_deliverables.map(d => (
                        <div key={d.id} className="px-4 py-3 border-b border-yellow-100 last:border-0 flex justify-between items-center text-sm">
                          <div>
                            <span className="font-bold text-dark dark:text-gray-100">{d.title || `تسليم #${d.id}`}</span>
                            {d.video_type && <span className="text-xs text-gray-400 mr-2">({d.video_type})</span>}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{d.delivery_date || 'بدون تاريخ'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* All-time totals */}
                  {deleteModal.deps.has_any_data && (
                    <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 mt-2">
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">إجمالي البيانات المرتبطة (كل الأوقات)</p>
                      <div className="grid grid-cols-4 gap-3 text-center">
                        <div>
                          <div className="text-lg font-black text-dark dark:text-gray-100">{deleteModal.deps.totals.total_projects}</div>
                          <div className="text-[10px] text-gray-400">مشروع</div>
                        </div>
                        <div>
                          <div className="text-lg font-black text-dark dark:text-gray-100">{deleteModal.deps.totals.total_subscriptions}</div>
                          <div className="text-[10px] text-gray-400">اشتراك</div>
                        </div>
                        <div>
                          <div className="text-lg font-black text-dark dark:text-gray-100">{deleteModal.deps.totals.total_invoices}</div>
                          <div className="text-[10px] text-gray-400">فاتورة</div>
                        </div>
                        <div>
                          <div className="text-lg font-black text-dark dark:text-gray-100">{deleteModal.deps.totals.total_deliverables}</div>
                          <div className="text-[10px] text-gray-400">تسليم</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            {!deleteModal.loading && (
              <div className="p-5 border-t border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5/30">
                <button onClick={() => setDeleteModal(null)} className="px-5 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 font-bold text-sm rounded-xl hover:bg-gray-200 transition-colors">
                  إلغاء
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                  className="px-6 py-2.5 bg-danger text-white font-bold text-sm rounded-xl hover:bg-red-700 shadow-md shadow-danger/20 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {deleteLoading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> جاري الحذف...</>
                  ) : (
                    <><Trash2 size={16} /> {deleteModal.deps?.can_delete ? 'حذف العميل' : 'حذف رغم الارتباطات'}</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full whitespace-nowrap text-right">
            <thead>
              <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                <th className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-gray-400">العميل</th>
                <th className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-gray-400">المشاريع / الاشتراكات</th>
                <th className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-gray-400">الرصيد المتبقي</th>
                <th className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 text-center">الحالة</th>
                <th className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 text-left">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.filter(c => {
                if (typeFilter === 'all') return true;
                if (typeFilter === 'subscriber') return c.type === 'subscriber';
                return c.type !== 'subscriber';
              }).map((c, i) => (
                <tr key={c.id} className="hover:bg-gray-50 dark:bg-white/5 dark:hover:bg-white/5/50 transition-colors group">
                  <td className="px-5 py-4">
                    <button
                      onClick={() => navigate('/clients/' + c.id)}
                      className="font-bold text-sm text-dark dark:text-gray-100 hover:text-primary transition-colors text-right"
                    >
                      {c.name}
                    </button>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400">{c.phone || c.email || 'لا يوجد تواصل'}</span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        c.type === 'subscriber'
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
                      }`}>
                        {c.type === 'subscriber' ? 'مشترك' : 'عميل'}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 mb-0.5">مشاريع</span>
                        <span className="font-bold text-sm text-dark dark:text-gray-100">{c.project_count}</span>
                      </div>
                      <div className="w-px h-6 bg-gray-200 dark:bg-white/10"></div>
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 mb-0.5">اشتراكات</span>
                        <span className="font-bold text-sm text-dark dark:text-gray-100">{c.active_subscriptions}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-black text-sm ${c.outstanding_iqd > 0 ? 'text-warning' : 'text-success'}`}>
                      {formatIQD(c.outstanding_iqd)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${c.status === 'active' ? 'bg-success/10 text-success border-success/20' : 'bg-danger/10 text-danger border-danger/20'}`}>
                      {c.status === 'active' ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-left">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {c.tracking_token ? (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/track/${c.tracking_token}`);
                            toast.success('تم نسخ الرابط');
                          }}
                          className="p-1.5 text-success bg-success/10 hover:bg-success hover:text-white rounded-lg transition-colors"
                          title="نسخ رابط التتبع"
                        >
                          <Link size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => generateTrackingLink(c.id)}
                          className="p-1.5 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 rounded-lg transition-colors"
                          title="إنشاء رابط تتبع"
                        >
                          <Link size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditClient(c); setEditForm({ name: c.name, phone: c.phone || '', email: c.email || '', type: c.type || 'company', status: c.status || 'active', notes: c.notes || '' }); }}
                        className="p-1.5 text-primary bg-primary/10 hover:bg-primary hover:text-white rounded-lg transition-colors"
                        title="تعديل"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteRequest(c)}
                        className="p-1.5 text-danger bg-danger/10 hover:bg-danger hover:text-white rounded-lg transition-colors"
                        title="حذف"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-gray-400">
                    <div className="w-16 h-16 bg-gray-50 dark:bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Users size={28} className="text-gray-300" />
                    </div>
                    <p className="text-sm font-medium">لا توجد سجلات عملاء لعرضها</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ClientsPage;
